import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownLeft, Car, AlertTriangle, DollarSign,
  FileSignature, Clock, CalendarPlus, CarFront, RefreshCw, MonitorSpeaker } from "lucide-react";

const KIND_META = {
  pickup:    { label: "PICKUP",    tone: "text-lime-300 border-lime-400/40 bg-lime-400/[0.07]",   icon: ArrowUpRight },
  return:    { label: "RETURN",    tone: "text-amber-300 border-amber-400/40 bg-amber-400/[0.07]", icon: ArrowDownLeft },
  chauffeur: { label: "CHAUFFEUR", tone: "text-cyan border-cyan/40 bg-cyan/[0.07]",                icon: Car },
};

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return "—"; }
};

const openBooking = (id) => {
  window.location.hash = "bookings";
  setTimeout(() => window.dispatchEvent(
    new CustomEvent("bo:jump-to-booking", { detail: { id } })
  ), 300);
};

const Counter = ({ icon: Icon, label, value, tone, onClick }) => (
  <button
    type="button"
    data-testid={`ops-counter-${label.toLowerCase().replace(/\s+/g, "-")}`}
    onClick={onClick}
    className={`text-left rounded-2xl border p-4 bg-black/30 hover:bg-black/40 transition ${tone}`}
  >
    <div className="flex items-center gap-2 label-caps text-[10px] opacity-70">
      <Icon size={11} /> {label}
    </div>
    <div className="text-white text-3xl font-serif mt-1">{value}</div>
  </button>
);

export default function OpsBoardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/admin/ops-board")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load ops board"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);  // auto-refresh every minute
    return () => clearInterval(t);
  }, []);

  const c = data?.counters;
  const nav = (tab) => {
    window.location.hash = tab;
  };

  return (
    <div data-testid="bo-ops-board-root" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps text-cyan text-[11px] mb-1">Live</div>
          <h2 className="font-serif text-2xl text-white flex items-center gap-2">
            <MonitorSpeaker className="text-cyan" size={20} /> Operations Board
          </h2>
          <div className="text-white/50 text-xs">
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · auto-refreshes every minute
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="ops-board-refresh"
          className="px-3 py-2 rounded-full border border-white/15 text-xs label-caps text-white/70 hover:text-white hover:border-white/30 inline-flex items-center gap-1.5"
        >
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="text-white/50 text-center py-12">Loading…</div>
      ) : data ? (
        <>
          <div className="rounded-2xl border border-white/10 bg-black/30 p-5">
            <div className="label-caps text-cyan text-[10px] mb-3">Today</div>
            {data.timeline.length === 0 ? (
              <div className="text-white/40 text-sm py-4">No pickups, returns, or chauffeur runs scheduled today.</div>
            ) : (
              <ol className="divide-y divide-white/8">
                {data.timeline.map((t) => {
                  const meta = KIND_META[t.kind] || KIND_META.pickup;
                  const Icon = meta.icon;
                  return (
                    <li key={`${t.kind}-${t.id}`}>
                      <button
                        type="button"
                        data-testid={`ops-timeline-${t.id}`}
                        onClick={() => openBooking(t.id)}
                        className="w-full text-left flex items-center gap-4 py-3 hover:bg-white/[0.03] transition px-2 rounded-lg"
                      >
                        <div className="text-white font-mono text-sm w-20 shrink-0">{fmtTime(t.at)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm truncate">
                            <span className="text-white/60">{t.vehicle || "—"}</span> · {t.customer || "—"}
                          </div>
                          <div className="text-white/40 text-[10px]">
                            {t.reservation_id || t.id}{t.phone ? " · " + t.phone : ""}
                          </div>
                        </div>
                        <span className={`inline-flex items-center gap-1 label-caps text-[10px] px-2 py-1 rounded-full border ${meta.tone}`}>
                          <Icon size={11} /> {meta.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Counter icon={AlertTriangle}   label="Needs Attention"    value={c.needs_attention}    tone="border-amber-400/30 text-amber-300"    onClick={() => nav("bookings")} />
            <Counter icon={DollarSign}      label="Payments Due"       value={c.payments_due}       tone="border-red-400/30 text-red-300"        onClick={() => nav("bookings")} />
            <Counter icon={FileSignature}   label="Contracts Missing"  value={c.contracts_missing}  tone="border-cyan/30 text-cyan"              onClick={() => nav("contracts")} />
            <Counter icon={Clock}           label="Overdue"            value={c.overdue}            tone="border-red-500/40 text-red-300"        onClick={() => nav("inspections")} />
            <Counter icon={CalendarPlus}    label="Extensions"         value={c.extensions}         tone="border-indigo-400/30 text-indigo-300"  onClick={() => nav("bookings")} />
            <Counter icon={CarFront}        label="Vehicles Available" value={c.vehicles_available} tone="border-lime-400/30 text-lime-300"      onClick={() => nav("fleet")} />
            <Counter icon={ArrowDownLeft}   label="Returning Today"    value={c.returning_today}    tone="border-white/15 text-white/70"         onClick={() => nav("inspections")} />
          </div>
        </>
      ) : null}
    </div>
  );
}
